import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseMutationOptions,
  type UseQueryOptions,
} from "@tanstack/react-query";
import type {
  ApiResponse,
  RecordAttendancePayload,
  UserDetailDTO,
  RevokeMembershipModel,
  AddMemberToClubModel,
  MemberIdentityPreviewDTO,
  MemberIdentityPreviewParams,
  RenewPayload,
  RenewRequest,
  AssignMembershipPayload,
  UpdateMembershipPayload,
  CorrectMembershipValidityPayload,
  MemberHistoryQueryParams,
  MemberBillingHistoryQueryParams,
  MemberAttendanceHistoryQueryParams,
  MemberMembershipHistoryResponse,
  MemberBillingHistoryResponse,
  MemberAttendanceHistoryResponse,
} from "@membentry/shared-types";
import {
  assignMembershipSchema,
  renewMembershipSchema,
  revokeMembershipSchema,
  type AssignMembershipParams,
  updateMembershipSchema,
  correctMembershipValiditySchema,
  type UpdateMembershipParams,
} from "@membentry/shared-validations";
import type { AxiosInstance } from "axios";
import {
  shouldRetryTransientRequest,
  transientRetryDelay,
} from "@membentry/shared-api";
import {
  CLUB_MEMBERS_QUERY_KEY,
  MY_CLUB_QUERY_KEY,
  CLUB_MEMBERSHIP_QUERY_KEY,
  MY_CLUBS_QUERY_KEY,
} from "../clubs/queryKeys";

export const MEMBERS_QUERY_KEY = "members";
export const VALIDATE_MEMBERSHIP_QUERY_KEY = "validate-membership";
export const USER_BY_CLUB_QUERY_KEY = "user-by-club";
export const CLUB_USER_QUERY_KEY = "club-user";
export const MEMBER_HISTORY_QUERY_KEY = "member-history";

export const memberHistoryQueryKeys = {
  all: [MEMBER_HISTORY_QUERY_KEY] as const,
  member: (clubId: number, userId: string) =>
    [MEMBER_HISTORY_QUERY_KEY, clubId, userId] as const,
  overview: (userId: string) => [CLUB_USER_QUERY_KEY, userId] as const,
  memberships: (
    clubId: number,
    userId: string,
    params?: MemberHistoryQueryParams,
  ) =>
    [
      MEMBER_HISTORY_QUERY_KEY,
      clubId,
      userId,
      "memberships",
      params?.page,
      params?.pageSize,
    ] as const,
  billing: (
    clubId: number,
    userId: string,
    params?: MemberBillingHistoryQueryParams,
  ) =>
    [
      MEMBER_HISTORY_QUERY_KEY,
      clubId,
      userId,
      "billing",
      params?.from,
      params?.to,
      params?.status,
      params?.page,
      params?.pageSize,
    ] as const,
  attendance: (
    clubId: number,
    userId: string,
    params?: MemberAttendanceHistoryQueryParams,
  ) =>
    [
      MEMBER_HISTORY_QUERY_KEY,
      clubId,
      userId,
      "attendance",
      params?.from,
      params?.to,
      params?.page,
      params?.pageSize,
    ] as const,
};

interface RecordAttendanceMutationParams {
  userId: string;
  clubId: number;
  /** Device-generated scan id; reuse it unchanged when retrying the same scan. */
  eventId?: string;
}

export interface UpdateMembershipVariables extends UpdateMembershipParams {
  userId: string;
}

type RecordAttendanceRequest = (
  client: AxiosInstance,
  params: RecordAttendanceMutationParams,
) => Promise<ApiResponse<RecordAttendancePayload>>;

type MutationSuccessHandler<TData, TVariables> = NonNullable<
  UseMutationOptions<TData, Error, TVariables>["onSuccess"]
>;

const withOnSuccess = <TData, TVariables>(
  base: MutationSuccessHandler<TData, TVariables>,
  extra?: MutationSuccessHandler<TData, TVariables>,
): MutationSuccessHandler<TData, TVariables> => {
  return (data, variables, onMutateResult, context) => {
    base(data, variables, onMutateResult, context);
    return extra?.(data, variables, onMutateResult, context);
  };
};

export function invalidateMemberHistory(
  queryClient: Pick<ReturnType<typeof useQueryClient>, "invalidateQueries">,
  clubId: number,
  userId: string,
) {
  queryClient.invalidateQueries({
    queryKey: memberHistoryQueryKeys.member(clubId, userId),
  });
  queryClient.invalidateQueries({
    queryKey: memberHistoryQueryKeys.overview(userId),
  });
}

export function useAddMemberToClubMutation(
  client: AxiosInstance,
  options?: Omit<
    UseMutationOptions<ApiResponse<UserDetailDTO>, Error, AddMemberToClubModel>,
    "mutationFn"
  >,
) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: AddMemberToClubModel) => {
      const { addMemberToClub } = await import("@membentry/shared-api");
      return addMemberToClub(client, data);
    },
    onSuccess: withOnSuccess((_data, variables) => {
      queryClient.invalidateQueries({
        queryKey: [CLUB_MEMBERS_QUERY_KEY, variables.clubId],
      });
    }, options?.onSuccess),
    ...options,
  });
}

export function useMemberIdentityPreviewMutation(
  client: AxiosInstance,
  options?: Omit<
    UseMutationOptions<
      ApiResponse<MemberIdentityPreviewDTO>,
      Error,
      MemberIdentityPreviewParams
    >,
    "mutationFn"
  >,
) {
  return useMutation({
    mutationFn: async (params: MemberIdentityPreviewParams) => {
      const { getMemberIdentityPreview } = await import(
        "@membentry/shared-api"
      );
      return getMemberIdentityPreview(client, params.clubId, {
        email: params.email,
        userId: params.userId,
      });
    },
    ...options,
  });
}

export type ValidateMembershipQueryOptions = Omit<
  UseQueryOptions<ApiResponse<UserDetailDTO>>,
  "queryKey" | "queryFn"
> & { scanId?: number };

export function useValidateMembershipQuery(
  client: AxiosInstance,
  userId: string,
  options?: ValidateMembershipQueryOptions,
) {
  const { scanId, ...queryOptions } = options ?? {};
  return useQuery({
    queryKey:
      scanId === undefined
        ? [VALIDATE_MEMBERSHIP_QUERY_KEY, userId]
        : [VALIDATE_MEMBERSHIP_QUERY_KEY, userId, scanId],
    queryFn: async () => {
      const { validateMembership } = await import("@membentry/shared-api");
      return validateMembership(client, userId);
    },
    enabled: !!userId,
    staleTime: 0,
    gcTime: 0,
    retry: shouldRetryTransientRequest,
    retryDelay: transientRetryDelay,
    refetchOnWindowFocus: false,
    ...queryOptions,
  });
}

export function useGetUserByClub(
  client: AxiosInstance,
  clubId: number,
  userId: string,
  options?: Omit<
    UseQueryOptions<ApiResponse<UserDetailDTO>>,
    "queryKey" | "queryFn"
  >,
) {
  return useQuery({
    queryKey: [USER_BY_CLUB_QUERY_KEY, clubId, userId],
    queryFn: async () => {
      const { getUserByClub } = await import("@membentry/shared-api");
      return getUserByClub(client, clubId, userId);
    },
    enabled: !!clubId && !!userId,
    ...options,
  });
}

export function useGetClubUser(
  client: AxiosInstance,
  userId: string,
  options?: Omit<
    UseQueryOptions<ApiResponse<UserDetailDTO>>,
    "queryKey" | "queryFn"
  >,
) {
  return useQuery({
    queryKey: [CLUB_USER_QUERY_KEY, userId],
    queryFn: async () => {
      const { getClubUser } = await import("@membentry/shared-api");
      return getClubUser(client, userId);
    },
    enabled: !!userId,
    ...options,
  });
}

export function useMemberMembershipHistoryQuery(
  client: AxiosInstance,
  clubId: number,
  userId: string,
  params?: MemberHistoryQueryParams,
  options?: Omit<
    UseQueryOptions<MemberMembershipHistoryResponse>,
    "queryKey" | "queryFn"
  >,
) {
  return useQuery({
    queryKey: memberHistoryQueryKeys.memberships(clubId, userId, params),
    queryFn: async () => {
      const { getMemberMembershipHistory } = await import(
        "@membentry/shared-api"
      );
      return getMemberMembershipHistory(client, clubId, userId, params);
    },
    ...options,
    enabled: clubId > 0 && !!userId && (options?.enabled ?? true),
  });
}

export function useMemberBillingHistoryQuery(
  client: AxiosInstance,
  clubId: number,
  userId: string,
  params?: MemberBillingHistoryQueryParams,
  options?: Omit<
    UseQueryOptions<MemberBillingHistoryResponse>,
    "queryKey" | "queryFn"
  >,
) {
  return useQuery({
    queryKey: memberHistoryQueryKeys.billing(clubId, userId, params),
    queryFn: async () => {
      const { getMemberBillingHistory } = await import("@membentry/shared-api");
      return getMemberBillingHistory(client, clubId, userId, params);
    },
    ...options,
    enabled: clubId > 0 && !!userId && (options?.enabled ?? true),
  });
}

export function useMemberAttendanceHistoryQuery(
  client: AxiosInstance,
  clubId: number,
  userId: string,
  params?: MemberAttendanceHistoryQueryParams,
  options?: Omit<
    UseQueryOptions<MemberAttendanceHistoryResponse>,
    "queryKey" | "queryFn"
  >,
) {
  return useQuery({
    queryKey: memberHistoryQueryKeys.attendance(clubId, userId, params),
    queryFn: async () => {
      const { getMemberAttendanceHistory } = await import(
        "@membentry/shared-api"
      );
      return getMemberAttendanceHistory(client, clubId, userId, params);
    },
    ...options,
    enabled: clubId > 0 && !!userId && (options?.enabled ?? true),
  });
}

export type RevokeMembershipVariables = RevokeMembershipModel & {
  /** One lowercase UUID v4 per revoke intent; reuse it unchanged only when retrying that intent. */
  idempotencyKey?: string;
};

export function useRevokeMembershipMutation(
  client: AxiosInstance,
  options?: Omit<
    UseMutationOptions<ApiResponse<string>, Error, RevokeMembershipVariables>,
    "mutationFn"
  >,
) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ idempotencyKey, ...data }: RevokeMembershipVariables) => {
      const { revokeMembership } = await import("@membentry/shared-api");
      const parsed = revokeMembershipSchema.parse(data) as RevokeMembershipModel;
      return idempotencyKey
        ? revokeMembership(client, parsed, { idempotencyKey })
        : revokeMembership(client, parsed);
    },
    onSuccess: withOnSuccess((_data, variables) => {
      queryClient.invalidateQueries({
        queryKey: [CLUB_MEMBERS_QUERY_KEY, variables.clubId],
      });
      queryClient.invalidateQueries({
        queryKey: [VALIDATE_MEMBERSHIP_QUERY_KEY, variables.userId],
      });
      invalidateMemberHistory(queryClient, variables.clubId, variables.userId);
    }, options?.onSuccess),
    ...options,
  });
}

export function useRecordAttendanceMutation(
  client: AxiosInstance,
  options?: Omit<
    UseMutationOptions<
      ApiResponse<RecordAttendancePayload>,
      Error,
      RecordAttendanceMutationParams
    >,
    "mutationFn"
  >,
) {
  const queryClient = useQueryClient();

  // WHY: a retry reuses the same eventId (idempotent server-side); without one the 90-minute window still suppresses a replayed scan.
  return useMutation({
    retry: shouldRetryTransientRequest,
    retryDelay: transientRetryDelay,
    mutationFn: async (params: RecordAttendanceMutationParams) => {
      const { recordAttendance: recordAttendanceRequest } = await import(
        "@membentry/shared-api"
      );
      const request = recordAttendanceRequest as unknown as RecordAttendanceRequest;
      return request(
        client,
        params,
      );
    },
    onSuccess: withOnSuccess((_data, variables) => {
      queryClient.invalidateQueries({
        queryKey: [VALIDATE_MEMBERSHIP_QUERY_KEY, variables.userId],
      });
      queryClient.invalidateQueries({ queryKey: [MY_CLUB_QUERY_KEY] });
      queryClient.invalidateQueries({
        queryKey: [CLUB_MEMBERS_QUERY_KEY, variables.clubId],
      });
      queryClient.invalidateQueries({
        queryKey: ["today-club-entries", "my-club"],
      });
      invalidateMemberHistory(queryClient, variables.clubId, variables.userId);
    }, options?.onSuccess),
    ...options,
  });
}

export function useRenewMembershipMutation(
  client: AxiosInstance,
  options?: Omit<
    UseMutationOptions<
      ApiResponse<RenewPayload>,
      Error,
      RenewMembershipVariables
    >,
    "mutationFn"
  >,
) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      userId,
      clubId,
      body,
      idempotencyKey,
    }: RenewMembershipVariables) => {
      const { renewMembership } = await import("@membentry/shared-api");
      const parsedBody = body ? renewMembershipSchema.parse(body) : undefined;
      return idempotencyKey
        ? renewMembership(client, userId, clubId, parsedBody, { idempotencyKey })
        : renewMembership(client, userId, clubId, parsedBody);
    },
    onSuccess: withOnSuccess((_data, variables) => {
      if (variables.userId) {
        queryClient.invalidateQueries({
          queryKey: [VALIDATE_MEMBERSHIP_QUERY_KEY, variables.userId],
        });
      }
      if (variables.clubId) {
        queryClient.invalidateQueries({
          queryKey: [CLUB_MEMBERS_QUERY_KEY, variables.clubId],
        });
        invalidateMemberHistory(queryClient, variables.clubId, variables.userId);
      }
    }, options?.onSuccess),
    ...options,
  });
}

export interface RenewMembershipVariables {
  userId: string;
  clubId?: number;
  body?: RenewRequest;
  idempotencyKey?: string;
}

export type AssignMembershipVariables = AssignMembershipParams & {
  idempotencyKey?: string;
};

export function useAssignMembershipMutation(
  client: AxiosInstance,
  options?: Omit<
    UseMutationOptions<
      ApiResponse<AssignMembershipPayload>,
      Error,
      AssignMembershipVariables
    >,
    "mutationFn"
  >,
) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ idempotencyKey, ...params }: AssignMembershipVariables) => {
      const { assignMembership } = await import("@membentry/shared-api");
      const parsed = assignMembershipSchema.parse(params);
      return idempotencyKey
        ? assignMembership(client, parsed, { idempotencyKey })
        : assignMembership(client, parsed);
    },
    onSuccess: withOnSuccess((_data, variables) => {
      queryClient.invalidateQueries({
        queryKey: [VALIDATE_MEMBERSHIP_QUERY_KEY, variables.userId],
      });
      queryClient.invalidateQueries({
        queryKey: [CLUB_MEMBERS_QUERY_KEY, variables.clubId],
      });
      queryClient.invalidateQueries({
        queryKey: [CLUB_MEMBERSHIP_QUERY_KEY, variables.clubId],
      });
      queryClient.invalidateQueries({ queryKey: [MY_CLUBS_QUERY_KEY] });
      invalidateMemberHistory(queryClient, variables.clubId, variables.userId);
    }, options?.onSuccess),
    ...options,
  });
}

export function useUpdateMembershipMutation(
  client: AxiosInstance,
  options?: Omit<
    UseMutationOptions<
      ApiResponse<UpdateMembershipPayload>,
      Error,
      UpdateMembershipVariables
    >,
    "mutationFn"
  >,
) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (params: UpdateMembershipVariables) => {
      const { updateMembership } = await import("@membentry/shared-api");
      const parsed = updateMembershipSchema.parse(params);
      return updateMembership(client, parsed.subscriptionId, {
        clubId: parsed.clubId,
        membershipPlanId: parsed.membershipPlanId,
        startDate: parsed.startDate,
      });
    },
    onSuccess: withOnSuccess((_data, variables) => {
      queryClient.invalidateQueries({
        queryKey: [VALIDATE_MEMBERSHIP_QUERY_KEY, variables.userId],
      });
      queryClient.invalidateQueries({
        queryKey: [CLUB_MEMBERS_QUERY_KEY, variables.clubId],
      });
      queryClient.invalidateQueries({
        queryKey: [CLUB_MEMBERSHIP_QUERY_KEY, variables.clubId],
      });
      queryClient.invalidateQueries({ queryKey: [MY_CLUBS_QUERY_KEY] });
      invalidateMemberHistory(queryClient, variables.clubId, variables.userId);
    }, options?.onSuccess),
    ...options,
  });
}

export interface CorrectMembershipValidityVariables {
  userId: string;
  clubId: number;
  subscriptionId: number;
  startDate: string;
  reason: string;
  expectedConcurrencyToken: string;
}

export function useCorrectMembershipValidityMutation(
  client: AxiosInstance,
  options?: Omit<
    UseMutationOptions<
      ApiResponse<CorrectMembershipValidityPayload>,
      Error,
      CorrectMembershipValidityVariables
    >,
    "mutationFn"
  >,
) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (params: CorrectMembershipValidityVariables) => {
      const { correctMembershipValidity } = await import("@membentry/shared-api");
      const parsed = correctMembershipValiditySchema.parse(params);
      return correctMembershipValidity(client, params.subscriptionId, parsed);
    },
    onSuccess: withOnSuccess((_data, variables) => {
      queryClient.invalidateQueries({
        queryKey: [VALIDATE_MEMBERSHIP_QUERY_KEY, variables.userId],
      });
      queryClient.invalidateQueries({
        queryKey: [CLUB_MEMBERS_QUERY_KEY, variables.clubId],
      });
      queryClient.invalidateQueries({
        queryKey: [CLUB_MEMBERSHIP_QUERY_KEY, variables.clubId],
      });
      queryClient.invalidateQueries({ queryKey: [MY_CLUBS_QUERY_KEY] });
      invalidateMemberHistory(queryClient, variables.clubId, variables.userId);
    }, options?.onSuccess),
    ...options,
  });
}

export function invalidateMembers(
  queryClient: ReturnType<typeof useQueryClient>,
  clubId?: number,
) {
  queryClient.invalidateQueries({ queryKey: [CLUB_MEMBERS_QUERY_KEY, clubId] });
}

export function invalidateUserByClub(
  queryClient: ReturnType<typeof useQueryClient>,
  clubId: number,
  userId: string,
) {
  queryClient.invalidateQueries({
    queryKey: [USER_BY_CLUB_QUERY_KEY, clubId, userId],
  });
}

export function invalidateValidateMembership(
  queryClient: ReturnType<typeof useQueryClient>,
  userId: string,
) {
  queryClient.invalidateQueries({
    queryKey: [VALIDATE_MEMBERSHIP_QUERY_KEY, userId],
  });
}
